<template>
    <div class="cfu-wrap">
        <!-- Drop zone -->
        <div
            class="cfu-dropzone"
            :class="{ 'cfu-dropzone--dragging': isDragging }"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onDrop"
            @click="$refs.fileInput.click()"
        >
            <input
                ref="fileInput"
                type="file"
                :multiple="multiple"
                :accept="acceptString"
                class="cfu-hidden-input"
                @change="onFileChange"
            />
            <i class="fal fa-cloud-upload cfu-dropzone-icon"></i>
            <p class="cfu-dropzone-text">{{ placeholder || 'Cliquez ou glissez vos fichiers ici' }}</p>
            <p v-if="acceptedExtensions && acceptedExtensions.length" class="cfu-dropzone-hint">
                Extensions acceptées : {{ acceptedExtensions.join(', ') }}
            </p>
        </div>

        <!-- Error -->
        <p v-if="errorMessage" class="cfu-error">{{ errorMessage }}</p>

        <!-- Preview: single -->
        <div v-if="!multiple && currentFiles" class="cfu-preview-single">
            <div class="cfu-preview-img" v-if="isImage(currentFiles)">
                <img :src="getPreviewUrl(currentFiles)" :alt="currentFiles.name" />
            </div>
            <div v-else class="cfu-preview-file-icon">
                <i class="fal fa-file"></i>
            </div>
            <div class="cfu-preview-info">
                <span class="cfu-preview-name">{{ currentFiles.name }}</span>
                <button class="cfu-preview-remove" @click.prevent="removeFile()">
                    <i class="fal fa-trash" style="color: #fff !important;"></i>
                </button>
            </div>
        </div>

        <!-- Preview: multiple -->
        <div v-if="multiple && currentFiles && currentFiles.length > 0" class="cfu-preview-grid">
            <div v-for="(file, index) in currentFiles" :key="index" class="cfu-preview-card">
                <div class="cfu-preview-img" v-if="isImage(file)">
                    <img :src="getPreviewUrl(file)" :alt="file.name" />
                </div>
                <div v-else class="cfu-preview-file-icon">
                    <i class="fal fa-file"></i>
                </div>
                <div class="cfu-preview-info">
                    <span class="cfu-preview-name">{{ file.name }}</span>
                    <button class="cfu-preview-remove" @click.prevent="removeFile(index)">
                        <i class="fal fa-trash" style="color: #fff !important;"></i>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import Compressor from 'compressorjs'

export default {
    name: 'BmvFileUpload',
    props: {
        multiple: {
            type: Boolean,
            default: false
        },
        max: {
            type: Number,
            default: 10
        },
        acceptedExtensions: {
            type: Array,
            default: null
        },
        compress: {
            type: Boolean,
            default: true
        },
        maxWidth: {
            type: Number,
            default: 1200
        },
        maxHeight: {
            type: Number,
            default: 1200
        },
        value: {
            default: null
        },
        placeholder: {
            type: String,
            default: null
        },
        previewBaseUrl: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            isDragging: false,
            errorMessage: '',
            currentFiles: null,
            baseUrl: this.previewBaseUrl || this.$store.state.baseURL,
            token: this.$store.state.auth.token
        }
    },
    created() {
        if (this.value) {
            if (this.multiple) {
                this.currentFiles = Array.isArray(this.value) ? [...this.value] : []
            } else {
                this.currentFiles = Array.isArray(this.value) && this.value.length > 0
                    ? this.value[0]
                    : (typeof this.value === 'object' ? this.value : null)
            }
        } else {
            this.currentFiles = this.multiple ? [] : null
        }
    },
    computed: {
        acceptString() {
            if (this.acceptedExtensions && this.acceptedExtensions.length) {
                return this.acceptedExtensions.map((e) => '.' + e.trim().toLowerCase()).join(',')
            }
            return '*'
        }
    },
    methods: {
        onFileChange(e) {
            this.processFiles(Array.from(e.target.files))
            this.$refs.fileInput.value = ''
        },
        onDrop(e) {
            this.isDragging = false
            this.processFiles(Array.from(e.dataTransfer.files))
        },
        processFiles(files) {
            if (!this.multiple) {
                this.handleFile(files[0], (result) => {
                    this.currentFiles = result
                    this.$emit('input', result)
                })
            } else {
                const toAdd = files.slice(0, this.max - (this.currentFiles ? this.currentFiles.length : 0))
                toAdd.forEach((file) => {
                    this.handleFile(file, (result) => {
                        this.currentFiles.push(result)
                        this.$emit('input', [...this.currentFiles])
                    })
                })
            }
        },
        handleFile(file, callback) {
            const ext = file.name.split('.').pop().toLowerCase()
            if (this.acceptedExtensions && !this.acceptedExtensions.map((e) => e.toLowerCase()).includes(ext)) {
                this.errorMessage = `Le format .${ext} n'est pas accepté.`
                return
            }
            this.errorMessage = ''

            const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
            if (this.compress && isImg) {
                new Compressor(file, {
                    quality: 0.8,
                    maxWidth: this.maxWidth,
                    maxHeight: this.maxHeight,
                    success: (compressed) => this.encodeFile(compressed, file.name, callback),
                    error: (e) => console.error(e)
                })
            } else {
                this.encodeFile(file, file.name, callback)
            }
        },
        encodeFile(file, name, callback) {
            const reader = new FileReader()
            reader.onload = (e) => {
                callback({
                    name: name,
                    ext: name.split('.').pop(),
                    encodedFile: e.target.result
                })
            }
            reader.readAsDataURL(file)
        },
        removeFile(index) {
            if (!this.multiple) {
                this.currentFiles = null
                this.$emit('input', null)
            } else {
                this.currentFiles.splice(index, 1)
                this.$emit('input', [...this.currentFiles])
            }
        },
        isImage(file) {
            if (!file || !file.name) return false
            const ext = file.name.split('.').pop().toLowerCase()
            return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
        },
        getPreviewUrl(file) {
            if (file.encodedFile) return file.encodedFile
            if (file.id) return `${this.baseUrl}/storage/${file.id}?token=${this.token}`
            return ''
        }
    }
}
</script>

<style scoped>
.cfu-hidden-input {
    display: none;
}

.cfu-dropzone {
    border: 2px dashed #d1d5db;
    border-radius: 12px;
    padding: 28px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
    background: #f8fafc;
}

.cfu-dropzone:hover {
    border-color: #0891B2;
    background: rgba(8, 145, 178, 0.02);
}

.cfu-dropzone--dragging {
    border-color: #0891B2;
    background: rgba(8, 145, 178, 0.04);
}

.cfu-dropzone-icon {
    font-size: 1.8rem;
    color: #cbd5e1;
    margin-bottom: 8px;
    display: block;
}

.cfu-dropzone:hover .cfu-dropzone-icon,
.cfu-dropzone--dragging .cfu-dropzone-icon {
    color: #0891B2;
}

.cfu-dropzone-text {
    font-size: 0.84rem;
    color: #64748b;
    margin: 0;
}

.cfu-dropzone-hint {
    font-size: 0.72rem;
    color: #94a3b8;
    margin: 6px 0 0;
}

.cfu-error {
    font-size: 0.75rem;
    color: #ef4444;
    margin: 6px 0 0;
    font-weight: 500;
}

/* Single preview */
.cfu-preview-single {
    margin-top: 12px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
}

.cfu-preview-img {
    height: 120px;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
}

.cfu-preview-img img {
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
}

.cfu-preview-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border-top: 1px solid #f1f5f9;
}

.cfu-preview-name {
    font-size: 0.78rem;
    color: #334155;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}

.cfu-preview-remove {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: none;
    background: #ef4444;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;
}

.cfu-preview-remove:hover {
    background: #dc2626;
    transform: translateY(-1px);
}

.cfu-preview-remove i {
    font-size: 0.7rem;
}

/* Grid preview */
.cfu-preview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
    margin-top: 12px;
}

.cfu-preview-card {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
}

.cfu-preview-card .cfu-preview-img {
    height: 100px;
}

.cfu-preview-file-icon {
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8fafc;
    color: #94a3b8;
    font-size: 1.5rem;
}
</style>
